interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  image?: string;
  imageId?: string;
  previewImage?: string;
  isGeneratingImage?: boolean;
  imageGenerationStartTime?: number;
  model?: string;
  userPrompt?: string;
  promptId?: string;
  fullPrompt?: string;
  error?: string;
  hasError?: boolean;
  style?: string; // Template ID from prompt.style
  templateUsed?: {
    id: string;
    title: string;
    icon?: string;
  };
}

interface StreamEvents {
  onSessionCreated?: (sessionId: string, sessionSlug: string, sessionTitle: string) => void;
  onMessage?: (messages: Message[]) => void;
  onComplete?: () => void;
  onError?: (error: Error) => void;
  onImageGenerationStarted?: () => void;
}

export class StreamHandler {
  private reader: ReadableStreamDefaultReader | null = null;
  private messages: Message[] = [];
  private isReading = false;
  private events: StreamEvents = {};
  private assistantMessage = '';
  private isGeneratingImage = false;

  constructor(events: StreamEvents = {}) {
    this.events = events;
  }

  // Set the stream reader and start processing
  setReader(reader: ReadableStreamDefaultReader | null) {
    if (!reader) {
      console.error('StreamHandler: Cannot set null reader');
      return;
    }
    this.reader = reader;
    if (!this.isReading) {
      this.startReading();
    }
  }

  // Add initial user message
  addUserMessage(content: string) {
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content,
      timestamp: new Date(),
    };
    this.messages = [...this.messages, userMessage];
    this.events.onMessage?.(this.messages);
  }

  // Add user message with template information
  addUserMessageWithTemplate(content: string, template: { id: string; title: string; icon?: string }) {
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content,
      timestamp: new Date(),
      templateUsed: template,
    };
    this.messages = [...this.messages, userMessage];
    this.events.onMessage?.(this.messages);
  }

  // Start reading from the stream
  private async startReading() {
    if (!this.reader || this.isReading) {
      console.warn('StreamHandler: Cannot start reading - no reader or already reading');
      return;
    }
    
    this.isReading = true;

    try {
      while (this.reader) {
        const { done, value } = await this.reader.read();
        if (done) break;

        const chunk = new TextDecoder().decode(value);
        const lines = chunk.split('\n').filter(line => line.trim());

        for (const line of lines) {
          try {
            const data = JSON.parse(line);
            this.handleStreamData(data);
          } catch (error) {
            // Ignore JSON parse errors for partial chunks
          }
        }
      }
    } catch (error) {
      console.error('StreamHandler: Error during reading:', error);
      // Update the current message with error state
      this.updateAssistantMessageWithError((error as Error).message);
      this.events.onError?.(error as Error);
    } finally {
      this.isReading = false;
      this.events.onComplete?.();
    }
  }

  // Handle different types of stream data
  private handleStreamData(data: any) {
    // Handle message types
    if (data.type === 'session_created' && data.sessionId && data.sessionSlug) {
      this.events.onSessionCreated?.(data.sessionId, data.sessionSlug, data.sessionTitle);
    } else if (data.type === 'assistant') {
      this.assistantMessage += data.content + ' ';
      this.updateAssistantMessage();
    } else if (data.type === 'image_generation_started') {
      this.isGeneratingImage = true;
      this.updateAssistantMessage();
      this.events.onImageGenerationStarted?.();
    } else if (data.type === 'image_preview_available' && data.previewUrl) {
      // Handle image preview update
      console.log('Image preview available:', data.previewUrl);
      this.updateAssistantMessageWithPreview(data.previewUrl);
    } else if (data.type === 'image_complete') {
      const imageId = data.imageId;
      if (imageId) {
        console.log('Image complete, clearing preview and setting final image:', imageId);
        this.isGeneratingImage = false;
        this.updateAssistantMessage(imageId, data.model, data.userPrompt, data.promptId, data.fullPrompt);
        this.events.onComplete?.();
      }
    } else if (data.error) {
      // Handle error response from image generation
      this.isGeneratingImage = false;
      this.updateAssistantMessageWithError(data.error);
      this.events.onComplete?.();
    }
  }

  // Update the assistant message in the messages array
  private updateAssistantMessage(imageId?: string, model?: string, userPrompt?: string, promptId?: string, fullPrompt?: string) {
    const lastMessage = this.messages[this.messages.length - 1];
    
    if (lastMessage && lastMessage.role === 'assistant') {
      // Update existing assistant message - preserve original timing info
      this.messages = this.messages.slice(0, -1).concat([{
        ...lastMessage,
        content: this.assistantMessage.trim(),
        isGeneratingImage: this.isGeneratingImage,
        // Preserve original imageGenerationStartTime, only set if starting generation, clear when done
        imageGenerationStartTime: this.isGeneratingImage 
          ? (lastMessage.imageGenerationStartTime || Date.now())
          : undefined,
        imageId: imageId || lastMessage.imageId,
        model: model || lastMessage.model,
        userPrompt: userPrompt || lastMessage.userPrompt,
        promptId: promptId || lastMessage.promptId,
        fullPrompt: fullPrompt || lastMessage.fullPrompt,
        // Clear preview when final image arrives
        previewImage: imageId ? undefined : lastMessage.previewImage,
      }]);
    } else {
      // Create new assistant message
      this.messages = this.messages.concat([{
        id: Date.now().toString(),
        role: 'assistant',
        content: this.assistantMessage.trim(),
        isGeneratingImage: this.isGeneratingImage,
        imageGenerationStartTime: this.isGeneratingImage ? Date.now() : undefined,
        timestamp: new Date(),
        imageId,
        model,
        userPrompt,
        promptId,
        fullPrompt,
      }]);
    }

    this.events.onMessage?.(this.messages);
  }

  // Update the assistant message with preview image
  private updateAssistantMessageWithPreview(previewUrl: string) {
    const lastMessage = this.messages[this.messages.length - 1];

    if (lastMessage && lastMessage.role === 'assistant') {
      // Update existing assistant message with preview
      this.messages = this.messages.slice(0, -1).concat([{
        ...lastMessage,
        previewImage: previewUrl,
        isGeneratingImage: true,
        imageGenerationStartTime: lastMessage.imageGenerationStartTime || Date.now(),
      }]);
      
      console.log('Updated message with preview:', this.messages[this.messages.length - 1].previewImage);
      this.events.onMessage?.(this.messages);
    } else {
      console.log('Creating new assistant message for preview');
      // Create assistant message if it doesn't exist yet
      this.messages = this.messages.concat([{
        id: Date.now().toString(),
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        previewImage: previewUrl,
        isGeneratingImage: true,
        imageGenerationStartTime: Date.now(),
      }]);
      this.events.onMessage?.(this.messages);
    }
  }

  // Update the current assistant message with error state
  private updateAssistantMessageWithError(errorMessage: string) {
    const lastMessage = this.messages[this.messages.length - 1];
    
    if (lastMessage && lastMessage.role === 'assistant') {
      // Update existing assistant message with error
      this.messages = this.messages.slice(0, -1).concat([{
        ...lastMessage,
        hasError: true,
        error: errorMessage,
        isGeneratingImage: false,
        imageGenerationStartTime: undefined,
      }]);
    } else {
      // Create new assistant message with error
      this.messages = this.messages.concat([{
        id: Date.now().toString(),
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        hasError: true,
        error: errorMessage,
        isGeneratingImage: false,
      }]);
    }

    this.events.onMessage?.(this.messages);
  }

  // Get current messages
  getMessages(): Message[] {
    return this.messages;
  }

  // Set messages (useful for initializing from existing data)
  setMessages(messages: Message[]) {
    this.messages = messages;
    this.events.onMessage?.(this.messages);
  }

  // Update with error (for external error handling)
  updateWithError(errorMessage: string) {
    this.updateAssistantMessageWithError(errorMessage);
  }

  // Clean up
  destroy() {
    this.reader = null;
    this.isReading = false;
    this.messages = [];
    this.assistantMessage = '';
    this.isGeneratingImage = false;
  }
}